import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Task } from '../types/common';
import { tasksApi } from '../api/endpoints/tasksApi';
import { CreateTaskRequest, UpdateTaskRequest } from '../api/types/TaskTypes';

interface TaskState {
  tasks: Task[];
  currentTask: Task | null;
  loading: boolean;
  error: string | null;
}

const initialState: TaskState = {
  tasks: [],
  currentTask: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (arg: string | { boardId: string; filters?: any }) => {
    const boardId = typeof arg === 'string' ? arg : arg.boardId;
    const filters = typeof arg === 'string' ? undefined : arg.filters;
    const response = await tasksApi.getTasks(boardId, filters);
    return response.tasks;
  }
);

export const fetchTask = createAsyncThunk('tasks/fetchTask', async (taskId: string) => {
  const response = await tasksApi.getTask(taskId);
  return response.task;
});

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (data: CreateTaskRequest) => {
    const response = await tasksApi.createTask(data);
    return response.task;
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async (
    { taskId, data }: { taskId: string; data: UpdateTaskRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await tasksApi.updateTask(taskId, data);
      return response.task;
    } catch (error: any) {
      // Without rejectWithValue, Redux Toolkit serializes the thrown error
      // down to {name, message, stack} before .unwrap() re-throws it -
      // silently dropping error.response.data.message (the actual backend
      // validation reason, e.g. "Assignee must be a member of this
      // project") in favor of axios's generic "Request failed with status
      // code 400". Extract it here so callers can show the real reason.
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Failed to update task',
      );
    }
  },
);

export const deleteTask = createAsyncThunk('tasks/deleteTask', async (taskId: string) => {
  await tasksApi.deleteTask(taskId);
  return taskId;
});

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearCurrentTask: (state) => {
      state.currentTask = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearTasks: (state) => {
      state.tasks = [];
    },
    /** Synchronous local patch for optimistic UI (e.g. drag-and-drop status
     * changes) - applied immediately, then overwritten by the authoritative
     * server response once updateTask resolves, or reverted by the caller
     * if it rejects. Does not touch `loading` so it never blanks the board. */
    patchTaskLocal: (
      state,
      action: PayloadAction<{ taskId: string; updates: Partial<Task> }>
    ) => {
      const { taskId, updates } = action.payload;
      const index = state.tasks.findIndex((task) => task.id === taskId);
      if (index !== -1) {
        state.tasks[index] = { ...state.tasks[index], ...updates };
      }
      if (state.currentTask?.id === taskId) {
        state.currentTask = { ...state.currentTask, ...updates };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action: PayloadAction<Task[]>) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tasks';
      });

    // Fetch single task
    builder
      .addCase(fetchTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTask.fulfilled, (state, action: PayloadAction<Task>) => {
        state.loading = false;
        state.currentTask = action.payload;
      })
      .addCase(fetchTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch task';
      });

    // Create task
    builder
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action: PayloadAction<Task>) => {
        state.loading = false;
        state.tasks.push(action.payload);
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create task';
      });

    // Update task
    // Deliberately does not set `loading` on pending/rejected: the task list
    // is already on screen (e.g. a Kanban board) when an update fires, and
    // callers apply an optimistic patchTaskLocal beforehand - toggling the
    // shared list-loading flag here would blank the whole board on every
    // drag-and-drop move while this is in flight.
    builder
      .addCase(updateTask.pending, (state) => {
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action: PayloadAction<Task>) => {
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = action.payload;
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || 'Failed to update task';
      });

    // Delete task
    builder
      .addCase(deleteTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.tasks = state.tasks.filter((task) => task.id !== action.payload);
        if (state.currentTask?.id === action.payload) {
          state.currentTask = null;
        }
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete task';
      });
  },
});

export const { clearCurrentTask, clearError, clearTasks, patchTaskLocal } = taskSlice.actions;
export default taskSlice.reducer;
